import { getAuthToken } from './authUtils';
import { API_URL } from '../config';
import { getDemoCalendarData, getDemoStrategistDecisions, getDemoStrategistGateReview } from './demoData';

function isDemoMode(): boolean {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem('demo_mode') === 'true';
}

const DEMO_RULES_STORAGE_KEY = 'signaldeck.strategyRules.v1';

function readDemoStrategistRules(): StrategistRule[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(DEMO_RULES_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeDemoStrategistRules(rules: StrategistRule[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(DEMO_RULES_STORAGE_KEY, JSON.stringify(rules));
  } catch {
    // Demo rule drafts are convenience state only.
  }
}

function normalizeStrategistRuleResponse(response: unknown): { rule: StrategistRule } {
  const maybeRule = (response as { rule?: unknown } | null)?.rule ?? response;
  if (!maybeRule || typeof maybeRule !== 'object') {
    throw new Error('Rule response was missing the created rule');
  }
  const rule = maybeRule as Partial<StrategistRule>;
  if (!rule.id || !rule.text || !rule.source || !rule.sourceTitle || !rule.status || !rule.createdAt) {
    throw new Error('Rule response was incomplete');
  }
  return { rule: rule as StrategistRule };
}

async function fetchApi<T>(
  path: string,
  params?: Record<string, string>,
  options?: { method?: string; body?: any },
): Promise<T> {
  let url = `${API_URL}/api${path}`;
  if (params) {
    const query = new URLSearchParams(params).toString();
    if (query) url += `?${query}`;
  }

  const headers: Record<string, string> = {};
  // Attach auth token for ALL requests (server enforces auth)
  const token = await getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Add Content-Type for requests with a body
  const fetchOptions: RequestInit = { method: options?.method || 'GET', headers };
  if (options?.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    fetchOptions.body = JSON.stringify(options.body);
  }

  const resp = await fetch(url, fetchOptions);
  if (!resp.ok) {
    const body = await resp.json().catch(() => ({}));
    const err = new Error(body.error || body.detail || `API error: ${resp.status}`);
    (err as any).code = body.code;
    (err as any).status = resp.status;
    (err as any).brokerAuthExpired = body.broker_auth_expired === true;
    (err as any).reconnectHint = body.reconnect_hint;
    if (body.broker_auth_expired === true) _emitBrokerAuthExpired(body.reconnect_hint);
    throw err;
  }
  const body = await resp.json();
  // Some routes return 200 with a broker_auth_expired flag embedded rather
  // than an HTTP error. Normalize both shapes into a thrown error so pages
  // can consistently render the reconnect UI.
  if (body && typeof body === 'object' && body.broker_auth_expired === true) {
    const err = new Error(body.reconnect_hint || body.error || 'Broker auth expired');
    (err as any).status = 401;
    (err as any).brokerAuthExpired = true;
    (err as any).reconnectHint = body.reconnect_hint;
    _emitBrokerAuthExpired(body.reconnect_hint);
    throw err;
  }
  return body;
}

// Emit a window-level event whenever fetchApi sees a broker-auth-expired flag.
// A top-level banner component (BrokerAuthBanner) listens and renders a
// persistent strip so every page gets the notification without having to
// individually handle the error case.
let _lastEmitTs = 0;
function _emitBrokerAuthExpired(hint?: string) {
  // Debounce: no more than one emit per 5s (avoids flood when a page polls
  // multiple endpoints in parallel and all 401).
  const now = Date.now();
  if (now - _lastEmitTs < 5000) return;
  _lastEmitTs = now;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('signaldeck:broker-auth-expired', { detail: { hint } }));
  }
}

export interface Account {
  equity: string;
  buying_power: string;
  cash: string;
  portfolio_value: string;
  last_equity: string;
  daily_pnl: string;
  daily_pnl_pct: string;
  status: string;
  pattern_day_trader: boolean;
  daytrade_count: number;
  currency: string;
}

export interface Position {
  symbol: string;
  qty: string;
  side: string;
  avg_entry_price: string;
  current_price: string;
  market_value: string;
  unrealized_pl: string;
  unrealized_plpc: string;
  change_today: string;
  asset_class: string;
}

export interface OrderLeg {
  id: string;
  symbol: string;
  side: string;
  qty: string;
  filled_qty: string;
  filled_avg_price: string | null;
  status: string;
  filled_at: string | null;
}

export interface Order {
  id: string;
  symbol: string;
  side: string;
  type: string;
  qty: string;
  filled_qty: string;
  filled_avg_price: string | null;
  status: string;
  submitted_at: string;
  filled_at: string | null;
  limit_price: string | null;
  stop_price: string | null;
  time_in_force: string;
  order_class?: string;
  legs?: OrderLeg[];
}

export interface NewsArticle {
  id: string;
  headline: string;
  summary: string;
  author: string;
  source: string;
  url: string;
  symbols: string[];
  created_at: string;
  updated_at: string;
  sentiment?: 'bullish' | 'bearish' | 'neutral';
  confidence?: number;
  impact?: 'high' | 'medium' | 'low';
  brief?: string;
}

export interface SectorImplication {
  sector: string;
  bias: 'bullish' | 'bearish';
  reason: string;
}

export interface TradeIdea {
  ticker: string;
  direction: 'long' | 'short';
  conviction: 'high' | 'medium' | 'low';
  thesis: string;
  timeframe: 'day_trade' | 'swing' | 'position';
  entry_price?: number;
  entry_date?: string;
}

export interface PlaybookScoreItem {
  ticker: string;
  direction: 'long' | 'short';
  conviction: 'high' | 'medium' | 'low';
  timeframe: 'day_trade' | 'swing' | 'position';
  entry_price: number;
  exit_price?: number;
  price_change_pct?: number;
  pnl_pct?: number;
  direction_correct?: boolean;
  entry_date: string;
  exit_date?: string;
  status: 'scored' | 'pending';
  hold_days?: number;
}

export interface PlaybookBreakdown {
  total: number;
  correct: number;
  accuracy_pct: number;
  avg_pnl_pct: number;
}

export interface PlaybookScorecard {
  total: number;
  correct: number;
  accuracy_pct: number;
  avg_pnl_pct: number;
  by_conviction: Record<string, PlaybookBreakdown>;
  by_direction: Record<string, PlaybookBreakdown>;
  by_timeframe: Record<string, PlaybookBreakdown>;
  current_streak: number;
  streak_type: string;
  recent_items: PlaybookScoreItem[];
  pending_items: PlaybookScoreItem[];
  pending_count: number;
  message?: string;
}

export interface SentimentResponse {
  articles: NewsArticle[];
  mood: 'bullish' | 'bearish' | 'neutral' | 'mixed';
  mood_score: number;
  summary: string;
  key_risks: string[];
  key_catalysts: string[];
  geopolitical_risk: number;
  geopolitical_summary: string;
  sector_implications: SectorImplication[];
  trading_playbook: TradeIdea[];
  analyzed_at: string;
}

export interface TradeAnalysis {
  recommendation: 'STRONG_BUY' | 'BUY' | 'HOLD' | 'CAUTION' | 'AVOID';
  confidence: number;
  reasons: string[];
  risk_factors: string[];
  suggested_stop_loss: number | null;
  suggested_take_profit: number | null;
  timing: string;
  timing_horizon: 'immediate' | 'days' | 'swing' | 'weeks';
  rsi2: number | null;
  rsi6: number | null;
  rsi14: number | null;
  mood_score: number;
  geo_risk: number;
  symbol: string;
  side: string;
  price: number | null;
  shortInterest?: ScreenerShortInterest | null;
}

export interface SecondOpinion {
  verdict: 'AGREE' | 'PARTIALLY_AGREE' | 'DISAGREE';
  confidence: number;
  summary: string;
  reasoning: string[];
  concerns: string[];
  would_take_trade: boolean;
  symbol: string;
  side: string;
  model: string;
}

export interface ScreenerClaudeResult {
  direction: 'LONG' | 'SHORT' | 'AVOID';
  confidence: number;
  reason: string;
  stopLoss: number | null;
  takeProfit: number | null;
  holdDays?: number;
  profitTargetPct?: number;
  earningsWarning?: string;
}

export interface ScreenerGptResult {
  verdict: 'AGREE' | 'DISAGREE';
  confidence: number;
  wouldTakeTrade: boolean;
  reason: string;
}

export interface ScreenerIntradayFeatures {
  rsi6: number | null;
  rsi14: number | null;
  ema_trend: 'BULLISH' | 'BEARISH' | 'FLAT';
  rel_vol: number;
  atr_pct: number;
  range_position: number;
  momentum_pct: number;
  alignment: 'ALIGNED' | 'DIVERGING' | 'NEUTRAL' | 'UNKNOWN';
  bar_count: number;
  timeframe?: '1Min' | '5Min' | '15Min' | '1Hour';
}

export interface ScreenerOptionsIntel {
  atm_iv: number;
  atm_iv_pct: number;
  iv_class: 'HIGH' | 'NORMAL' | 'LOW';
  near_expiry: string;
  near_dte: number;
  put_call_ratio: number | null;
  pc_signal: 'BULLISH' | 'NEUTRAL' | 'BEARISH' | 'UNKNOWN';
  total_call_oi: number;
  total_put_oi: number;
  contracts_seen: number;
}

export interface ScreenerShortInterest {
  pct_float: number | null;
  days_to_cover: number | null;
  pct_change: number | null;
  short_shares: number | null;
  bucket: 'HIGH' | 'ELEVATED' | 'NORMAL' | 'LOW';
}

export interface MarketRegime {
  name: 'RISK_ON' | 'RISK_OFF' | 'NEUTRAL' | 'VOLATILE' | 'STRONG_TREND_UP' | 'STRONG_TREND_DOWN';
  mood_score: number;
  geo_risk: number;
  bias: string;
  weights: string;
}

export interface ScreenerSetupHistory {
  signal: string;
  direction: 'LONG' | 'SHORT';
  triggers: number;
  hits: number;
  hit_rate: number;
  avg_return_pct: number;
  forward_days: number;
  threshold_pct: number;
  lookback_bars: number;
  recent_window: number;
  recent_hits: number;
}

export interface ScreenerResult {
  symbol: string;
  sector: string;
  price: number;
  rsi2: number | null;
  rsi6: number | null;
  rsi14: number | null;
  changePct: number;
  signal: string;
  earningsStatus?: 'CLEAR' | 'WARN' | 'BLOCK';
  earningsDate?: string | null;
  earningsTradingDays?: number | null;
  earningsMessage?: string;
  intraday?: ScreenerIntradayFeatures | null;
  options?: ScreenerOptionsIntel | null;
  shortInterest?: ScreenerShortInterest | null;
  setupHistory?: ScreenerSetupHistory | null;
  claude: ScreenerClaudeResult;
  gpt: ScreenerGptResult;
  consensus: 'LONG' | 'SHORT';
  consensusTag: 'BOTH_AGREE' | 'LEAN_AGREE' | 'CLAUDE_ONLY';
  combinedConfidence: number;
}

export interface ScreenerResponse {
  results: ScreenerResult[];
  scannedCount: number;
  consensusCount: number;
  earningsBlockedCount?: number;
  scannedAt: string;
  sectors: Record<string, string[]>;
  regime?: MarketRegime;
  partial?: boolean;
  elapsedMs?: number;
}

export interface OilPrice {
  price: number;
  prev?: number;
  change: number;
  change_pct: number;
  date?: string;
  error?: string;
}

export interface MacroIndicator {
  value: number;
  prev?: number;
  change: number;
  date: string;
  error?: string;
}

export interface GdeltEvent {
  headline: string;
  source: string;
  url: string;
  date: string;
}

export interface ChokepointData {
  label: string;
  current_vessels?: number;
  tankers?: number;
  avg_7d?: number;
  avg_30d?: number;
  delta_pct?: number;
  status: string;
  last_snapshot?: string;
  data_points?: number;
  error?: string;
}

export interface MacroIntelligence {
  gdelt: {
    events: GdeltEvent[];
    event_count: number;
    top_themes: string[];
  };
  oil: {
    wti?: OilPrice;
    brent?: OilPrice;
    error?: string;
  };
  macro: {
    cpi?: MacroIndicator;
    yield_curve?: MacroIndicator;
    usd_index?: MacroIndicator;
    error?: string;
  };
  shipping?: {
    chokepoints: Record<string, ChokepointData>;
  };
  fetched_at: string;
}

export interface SymbolSnapshot {
  price: number;
  prev_close: number;
  change: number;
  change_pct: number;
  day_open: number | null;
  day_high: number | null;
  day_low: number | null;
  volume: number | null;
  latest_timestamp: string;
  minute_close: number | null;
  minute_volume: number | null;
}

export interface PredictionScore {
  date: string;
  predicted_mood: string;
  predicted_score: number;
  predicted_bias: string | null;
  actual_open: number;
  actual_close: number;
  actual_change: number;
  actual_change_pct: number;
  direction_correct: boolean;
  magnitude_error: number;
  grade: string;
  scored_at: string;
}

export interface PredictionHistoryItem {
  date: string;
  mood: string;
  mood_score: number;
  summary: string;
  bias: string | null;
  bias_confidence: number;
  analyzed_at: string;
  headline_count: number;
  bullish_count: number;
  bearish_count: number;
  neutral_count: number;
  score: PredictionScore | null;
}

export interface Scorecard {
  total: number;
  correct: number;
  accuracy_pct: number;
  avg_magnitude_error: number;
  grades: Record<string, number>;
  current_streak: number;
  streak_type: string;
  recent_scores: PredictionScore[];
}

export interface VixData {
  value: number | null;
  prev_close: number;
  change: number;
  change_pct: number;
  level: 'calm' | 'normal' | 'elevated' | 'high' | 'extreme';
  error?: string;
}

export interface PutCallRatioData {
  pcr: number | null;
  put_oi: number;
  call_oi: number;
  label: 'extreme_bullish' | 'bullish' | 'neutral' | 'bearish' | 'extreme_bearish';
  error?: string;
}

export interface ShortVolumeData {
  short_ratio: number | null;
  short_ratio_pct: number;
  short_volume: number;
  total_volume: number;
  label: 'low' | 'normal' | 'moderate' | 'heavy';
  data_date: string;
  error?: string;
}

export interface MarketPulse {
  vix: VixData;
  put_call_ratio: PutCallRatioData;
  short_volume: ShortVolumeData;
  fetched_at: string;
}

export interface EarningsItem {
  symbol: string;
  date: string;
  hour: string;
  eps_estimate: number | null;
  eps_actual: number | null;
  revenue_estimate: number | null;
  revenue_actual: number | null;
  quarter: number | null;
  year: number | null;
}

export interface EarningsCalendar {
  upcoming: EarningsItem[];
  past_recent: EarningsItem[];
  count: number;
  symbols_tracked: number | null;
  filter_applied: boolean;
  filter_symbols: string[];
  date_range: { from: string; to: string };
  fetched_at: string;
}

// ─── Backtest Types ─────────────────────────────────────────

export interface BacktestTrade {
  entryDate: string;
  exitDate: string;
  entryPrice: number;
  exitPrice: number;
  side?: string;
  shares?: number;
  symbol?: string;
  direction?: string;
  strategy?: string;
  sector?: string;
  qty?: number;
  pnl: number;
  pnlPct: number;
  holdDays: number;
  exitReason: string;
  sdScore?: number;
  sdScoreExit?: number;
}

export interface BacktestMetricsResult {
  totalTrades: number;
  winners: number;
  losers: number;
  winRate: number;
  totalPnl: number;
  totalReturn: number;
  profitFactor: number;
  avgPnl: number;
  avgWin: number;
  avgLoss: number;
  largestWin: number;
  largestLoss: number;
  maxDrawdown: number;
  sharpe: number;
  avgHoldDays: number;
  maxWinStreak: number;
  maxLossStreak: number;
  startCapital: number;
  endCapital: number;
  yearly: Record<string, { trades: number; pnl: number; wins: number }>;
  error?: string;
}

export interface BacktestResult {
  strategy: string;
  symbol?: string;
  years?: number;
  barsUsed?: number;
  symbolCount?: number;
  days?: number;
  dateRange: { start: string; end: string };
  metrics: BacktestMetricsResult;
  trades: BacktestTrade[];
  tradesTruncated: boolean;
  equityCurve: { date: string; equity: number }[];
  strategyBreakdown?: Record<
    string,
    { trades: number; wins: number; winRate: number; pnl: number; longs: number; shorts: number }
  >;
  cached?: boolean;
  error?: string;
  scoreSeries?: { date: string; score: number }[];
  pillarConfig?: Record<string, number>;
}

export interface OosValidationResult {
  strategy: 'oos-validation';
  baseStrategy: string;
  symbol: string;
  years: number;
  splitRatio: number;
  splitDate: string;
  totalBars: number;
  inSampleBars: number;
  outOfSampleBars: number;
  inSample: BacktestMetricsResult;
  outOfSample: BacktestMetricsResult;
  overfitScore: number;
  cached?: boolean;
  error?: string;
}

export interface ParamSweepEntry {
  params: Record<string, number>;
  metricValue: number;
  totalTrades: number;
  winRate: number;
  sharpe: number;
  totalReturn: number;
  maxDrawdown: number;
  profitFactor: number;
}

export interface ParamSweepResult {
  strategy: 'param-sweep';
  baseStrategy: string;
  symbol: string;
  years: number;
  metric: string;
  sweepParams: string[];
  totalCombinations: number;
  evaluated: number;
  truncated: boolean;
  best: ParamSweepEntry | null;
  results: ParamSweepEntry[];
  heatmapData?: {
    xParam: string;
    yParam: string;
    xValues: number[];
    yValues: number[];
    grid: number[][];
  };
  lineData?: {
    param: string;
    values: number[];
    metrics: number[];
  };
  cached?: boolean;
  error?: string;
}

export interface ClosePositionResponse {
  success?: boolean;
  order?: {
    id: string;
    symbol: string;
    qty: string;
    side: string;
    type: string;
    status: string;
  };
  error?: string;
  detail?: string;
}

// ─── Calibration types ──────────────────────────────────────

// ─── Daily Edge types ───────────────────────────────────

export interface DailyEdgePick {
  symbol: string;
  sector: string;
  direction: 'LONG' | 'SHORT';
  confidence: number;
  entryPrice: number;
  stopLoss: number | null;
  takeProfit: number | null;
  reason: string;
  rsi2: number | null;
  rsi6: number | null;
  rsi14: number | null;
  holdDays: number | null;
  profitTargetPct: number | null;
}

export interface DailyEdgePanel {
  scannedAt: string | null;
  symbolsScanned: number;
  picks: DailyEdgePick[];
}

export interface DailyEdgeResponse {
  date: string;
  premarket: DailyEdgePanel;
  powerHour: DailyEdgePanel;
}

export interface CalibrationBucket {
  count: number;
  wins?: number;
  losses?: number;
  neutrals?: number;
  winRate: number | null;
  avgPnl1h: number | null;
  avgPnl4h: number | null;
}

export interface UsageBucket {
  count: number;
  limit: number;
  remaining: number;
  lastAt: string | null;
}

export interface UsageToday {
  date: string;
  actions: Record<string, UsageBucket>;
  error?: string;
}

export interface CalibrationSummary {
  days: number;
  totalScored: number;
  buckets: {
    overall: CalibrationBucket;
    byConfidence: Record<string, CalibrationBucket>;
    byRegime: Record<string, CalibrationBucket>;
    bySessionPhase: Record<string, CalibrationBucket>;
    byShortInterestBucket: Record<string, CalibrationBucket>;
    bySource: Record<string, CalibrationBucket>;
  };
  generatedAt: string;
}

// ─── Service ─────────────────────────────────────────────────

// Helper: inject mode into params if provided
function withMode(params?: Record<string, string>, mode?: string): Record<string, string> | undefined {
  if (!mode) return params;
  return { ...params, mode };
}

export const alpacaService = {
  getAccount: (mode?: string) => fetchApi<Account>('/account', withMode(undefined, mode)),
  getPositions: (mode?: string) =>
    fetchApi<{ positions: Position[]; count: number }>('/positions', withMode(undefined, mode)),
  getOrders: (status = 'all', limit = '500', after?: string, until?: string, mode?: string) => {
    const params: Record<string, string> = { status, limit };
    if (after) params.after = after;
    if (until) params.until = until;
    if (mode) params.mode = mode;
    return fetchApi<{ orders: Order[]; count: number }>('/orders', params);
  },
  getNews: (symbols = 'SPY,QQQ', limit = '20') =>
    fetchApi<{ articles: NewsArticle[]; count: number }>('/news', { symbols, limit }),
  getSentiment: (symbols = 'SPY,QQQ', limit = '15') => fetchApi<SentimentResponse>('/sentiment', { symbols, limit }),
  getTradeAnalysis: (symbol: string, side: string, action?: string, position?: string) =>
    fetchApi<TradeAnalysis>('/trade-analysis', {
      symbol,
      side,
      ...(action ? { action } : {}),
      ...(position ? { position } : {}),
    }),
  getSecondOpinion: (symbol: string, side: string, price: string, claudeAnalysis: TradeAnalysis) =>
    fetchApi<SecondOpinion>('/second-opinion', undefined, {
      method: 'POST',
      body: { symbol, side, price, claude_analysis: claudeAnalysis },
    }),
  getSnapshot: (symbols = 'SPY,QQQ,TLT') =>
    fetchApi<{ snapshots: Record<string, SymbolSnapshot>; count: number }>('/snapshot', { symbols }),
  getPredictionHistory: (limit = '30') =>
    fetchApi<{ predictions: PredictionHistoryItem[]; count: number }>('/predictions/history', { limit }),
  scorePrediction: (date?: string) => {
    const params: Record<string, string> = {};
    if (date) params.date = date;
    return fetchApi<PredictionScore>('/predictions/score', params);
  },
  getScorecard: () => fetchApi<Scorecard>('/predictions/scorecard'),
  getPlaybookScorecard: () => fetchApi<PlaybookScorecard>('/predictions/playbook-scorecard'),
  scorePlaybook: (date?: string) => {
    const params: Record<string, string> = {};
    if (date) params.date = date;
    return fetchApi<any>('/predictions/playbook-score', params);
  },
  getMarketPulse: (mode?: string) => fetchApi<MarketPulse>('/market-pulse', withMode(undefined, mode)),
  getEarningsCalendar: (symbols?: string[]) =>
    fetchApi<EarningsCalendar>(
      '/earnings-calendar',
      symbols && symbols.length > 0 ? { symbols: symbols.join(',') } : undefined,
    ),
  getMacroIntelligence: () => fetchApi<MacroIntelligence>('/intelligence/macro'),

  // Position management
  closePosition: (symbol: string, mode?: string, opts?: { qty?: number; percentage?: number }) => {
    const params: Record<string, string> = {};
    if (opts?.qty != null && opts.qty > 0) params.qty = String(opts.qty);
    else if (opts?.percentage != null && opts.percentage > 0 && opts.percentage <= 100)
      params.percentage = String(opts.percentage);
    return fetchApi<ClosePositionResponse>(`/positions/${encodeURIComponent(symbol)}`, withMode(params, mode), {
      method: 'DELETE',
    });
  },

  // Strategy Builder endpoints
  deployStrategy: (payload: { name: string; config: Record<string, any>; mode?: string }) =>
    fetchApi<{ status: string }>('/strategy/deploy', undefined, { method: 'POST', body: payload }),
  stopStrategy: () => fetchApi<{ status: string }>('/strategy/stop', undefined, { method: 'POST', body: {} }),
  getStrategyStatus: () => fetchApi<any>('/strategy/status'),

  // Backtesting
  runBacktest: (payload: {
    strategyType: string;
    config: Record<string, any>;
    symbol?: string;
    years?: number;
    initialCapital?: number;
  }) => fetchApi<BacktestResult>('/backtest', undefined, { method: 'POST', body: payload }),

  runParamSweep: (payload: {
    baseStrategy: string;
    config: Record<string, any>;
    sweepParams: Record<string, { min: number; max: number; step: number }>;
    metric?: string;
    symbol?: string;
    years?: number;
    initialCapital?: number;
  }) =>
    fetchApi<ParamSweepResult>('/backtest', undefined, {
      method: 'POST',
      body: { ...payload, strategyType: 'param-sweep' },
    }),

  runOosValidation: (payload: {
    baseStrategy: string;
    config: Record<string, any>;
    symbol?: string;
    years?: number;
    splitRatio?: number;
    initialCapital?: number;
  }) =>
    fetchApi<OosValidationResult>('/backtest', undefined, {
      method: 'POST',
      body: { ...payload, strategyType: 'oos-validation' },
    }),

  // Scanner endpoints
  scanRsi: (symbols: string) => fetchApi<ScanResponse>('/scanner', { symbols }),

  smartScan: (symbols: string, filters: SmartScanFilter) =>
    fetchApi<SmartScanResponse>('/smart-scanner', {
      symbols,
      filters: JSON.stringify(filters),
    }),

  placeOrder: (order: PlaceOrderRequest) =>
    fetchApi<PlaceOrderResponse>('/orders/place', undefined, { method: 'POST', body: order }),

  cancelOrder: (orderId: string, mode?: string) =>
    fetchApi<{ success: boolean; order_id: string }>(`/orders/${orderId}`, withMode(undefined, mode), {
      method: 'DELETE',
    }),

  // Error log
  getErrorLog: (params?: { source?: string; limit?: string }) =>
    fetchApi<{ entries: ErrorLogEntry[]; count: number }>('/error-log', params),

  // Watchlists
  getWatchlists: () => fetchApi<{ watchlists: WatchlistItem[] }>('/watchlists'),
  saveWatchlist: (name: string, symbols: string[]) =>
    fetchApi<{ success: boolean; name: string; symbols: string[] }>('/watchlists', undefined, {
      method: 'PUT',
      body: { name, symbols },
    }),
  deleteWatchlist: (name: string) =>
    fetchApi<{ success: boolean }>('/watchlists', undefined, { method: 'DELETE', body: { name } }),
  // Strategist endpoints
  getStrategistConfig: () => fetchApi<StrategistConfig>('/strategist/config'),
  saveStrategistConfig: (config: Partial<StrategistConfig>) =>
    fetchApi<{ success: boolean }>('/strategist/config', undefined, { method: 'PUT', body: config }),
  getStrategistRules: async () => {
    const token = await getAuthToken();
    if (isDemoMode() || !token) {
      const rules = readDemoStrategistRules();
      return { rules, count: rules.length };
    }
    return fetchApi<{ rules: StrategistRule[]; count: number }>('/strategist/rules');
  },
  createStrategistRule: async (rule: Pick<StrategistRule, 'text' | 'source' | 'sourceTitle'>) => {
    const token = await getAuthToken();
    if (isDemoMode() || !token) {
      const now = new Date().toISOString();
      const created: StrategistRule = {
        id: `RULE#${Date.now().toString(36)}#${Math.random().toString(36).slice(2, 8)}`,
        text: rule.text,
        source: rule.source,
        sourceTitle: rule.sourceTitle,
        status: 'pending',
        createdAt: now,
        updatedAt: now,
      };
      writeDemoStrategistRules([created, ...readDemoStrategistRules()]);
      return { rule: created };
    }
    const resp = await fetchApi<unknown>('/strategist/rules', undefined, { method: 'POST', body: rule });
    return normalizeStrategistRuleResponse(resp);
  },
  updateStrategistRule: async (id: string, updates: Pick<StrategistRule, 'status'>) => {
    const token = await getAuthToken();
    if (isDemoMode() || !token) {
      const updatedAt = new Date().toISOString();
      let updated: StrategistRule | null = null;
      const rules = readDemoStrategistRules().map((rule) => {
        if (rule.id !== id) return rule;
        updated = { ...rule, status: updates.status, updatedAt };
        return updated;
      });
      if (!updated) {
        throw new Error('Rule not found');
      }
      writeDemoStrategistRules(rules);
      return { rule: updated };
    }
    return fetchApi<{ rule: StrategistRule }>(`/strategist/rules/${encodeURIComponent(id)}`, undefined, {
      method: 'PATCH',
      body: updates,
    });
  },
  getStrategistActions: (status?: string) =>
    fetchApi<{ actions: StrategistAction[]; count: number }>('/strategist/actions', status ? { status } : undefined),
  approveStrategistAction: (actionId: string) =>
    fetchApi<{ success: boolean; orderId?: string }>('/strategist/actions/approve', undefined, {
      method: 'POST',
      body: { actionId },
    }),
  rejectStrategistAction: (actionId: string) =>
    fetchApi<{ success: boolean }>('/strategist/actions/reject', undefined, { method: 'POST', body: { actionId } }),
  getStrategistDecisions: async (status?: string, limit?: number) => {
    const token = await getAuthToken();
    if (isDemoMode() || !token) {
      const decisions = getDemoStrategistDecisions()
        .filter((decision) => (status ? decision.status === status : true))
        .slice(0, limit ?? 100);
      return { decisions, count: decisions.length };
    }
    const params: Record<string, string> = {};
    if (status) params.status = status;
    if (limit) params.limit = String(limit);
    return fetchApi<{ decisions: StrategistDecision[]; count: number }>(
      '/strategist/decisions',
      Object.keys(params).length ? params : undefined,
    );
  },
  getStrategistReplay: async (decisionSK: string) => {
    const token = await getAuthToken();
    if (isDemoMode() || !token) {
      const decision = getDemoStrategistDecisions().find((item) => item.SK === decisionSK);
      if (!decision) {
        throw new Error('Decision not found');
      }
      const activeRules = readDemoStrategistRules().filter((rule) => rule.status === 'active');
      const ruleTimeline: ReplayTimelineItem[] = activeRules.length
        ? [
            {
              key: 'rule_guardrails',
              label: 'Rule Guardrails',
              status: 'active',
              at: decision.createdAt,
              summary: `${activeRules.length} active rule${activeRules.length === 1 ? '' : 's'} available to this decision.`,
              details: activeRules
                .slice(0, 3)
                .map((rule) => rule.text)
                .join('; '),
            },
          ]
        : [];
      return {
        decision,
        action: null,
        validation: null,
        run: null,
        gateLog: null,
        gateEvents: [],
        postmortem: null,
        activeRules,
        ruleScope: 'current_active' as const,
        timeline: [
          {
            key: 'scanner',
            label: 'Scanner Signal',
            status: 'demo',
            at: decision.createdAt,
            summary: `${decision.strategy || 'UNKNOWN'} flagged ${decision.symbol}.`,
            details: decision.reasoning,
          },
          {
            key: 'analyst',
            label: 'Analyst Decision',
            status: 'demo',
            at: decision.createdAt,
            summary: `${decision.confidence}% confidence for ${decision.qty} share${decision.qty === 1 ? '' : 's'}.`,
            details: decision.reasoning,
          },
          {
            key: 'risk_plan',
            label: 'Risk Plan',
            status: 'demo',
            at: decision.createdAt,
            summary: `Entry ${decision.entryPrice}, exit ${decision.exitPrice ?? 'not recorded'}.`,
            details: `Strategy ${decision.strategy || 'not recorded'}.`,
          },
          ...ruleTimeline,
          {
            key: 'outcome',
            label: 'Outcome',
            status: decision.status,
            at: decision.closedAt,
            summary:
              decision.pnlPct != null
                ? `Closed with ${decision.pnlPct > 0 ? '+' : ''}${decision.pnlPct.toFixed(1)}% P&L.`
                : 'Closed.',
            details: decision.pnl != null ? `P&L ${decision.pnl}.` : '',
          },
        ],
      };
    }
    return fetchApi<StrategistReplay>('/strategist/replay', { decisionSK });
  },
  getStrategistGateReview: async (params?: { limit?: number; gate?: string; symbol?: string }) => {
    const token = await getAuthToken();
    if (isDemoMode() || !token) {
      const demo = getDemoStrategistGateReview();
      const candidates = demo.candidates.filter((candidate) => {
        if (params?.gate && candidate.lastGate !== params.gate) return false;
        if (params?.symbol && candidate.symbol !== params.symbol.toUpperCase()) return false;
        return true;
      });
      return {
        ...demo,
        candidates,
        gates: Array.from(new Set(candidates.map((candidate) => candidate.lastGate))).sort(),
        count: candidates.length,
        diagnostics: demo.diagnostics,
      };
    }
    const query: Record<string, string> = {};
    if (params?.limit) query.limit = String(params.limit);
    if (params?.gate) query.gate = params.gate;
    if (params?.symbol) query.symbol = params.symbol;
    return fetchApi<StrategistGateReview>('/strategist/gates', Object.keys(query).length ? query : undefined);
  },
  updateStrategistGateFollowups: async () => {
    const token = await getAuthToken();
    if (isDemoMode() || !token) {
      return { logsUpdated: 1, outcomesUpdated: 2, skipped: 1 };
    }
    return fetchApi<{ logsUpdated: number; outcomesUpdated: number; skipped: number }>(
      '/strategist/gates/followups',
      undefined,
      { method: 'POST' },
    );
  },
  getStrategistRuns: (limit = 20) =>
    fetchApi<{ runs: StrategistRun[]; count: number }>('/strategist/runs', { limit: String(limit) }),
  getStrategistSectors: () => fetchApi<SectorSentimentResponse>('/strategist/sectors'),

  // ── Scalper (single-symbol intraday AI; multi-instance) ──
  getScalperInstances: () => fetchApi<{ instances: ScalperInstance[]; default: string }>('/scalper/instances'),
  getScalperPrices: () => fetchApi<ScalperPricesResponse>('/scalper/prices'),
  getScalperConfig: (instance?: string) =>
    fetchApi<ScalperConfig>('/scalper/config', instance ? { instance } : undefined),
  saveScalperConfig: (patch: Partial<{ enabled: boolean; symbol: string }>, instance?: string) =>
    fetchApi<{ success: boolean }>('/scalper/config', instance ? { instance } : undefined, {
      method: 'PUT',
      body: patch,
    }),
  saveScalperCredentials: (creds: { api_key: string; secret_key: string; mode?: 'paper' }) =>
    fetchApi<{ success: boolean; mode: string }>('/scalper/credentials', undefined, {
      method: 'POST',
      body: { mode: 'paper', ...creds },
    }),
  deleteScalperCredentials: () =>
    fetchApi<{ success: boolean }>('/scalper/credentials', undefined, { method: 'DELETE' }),
  getScalperStatus: (instance?: string) =>
    fetchApi<ScalperStatus>('/scalper/status', instance ? { instance } : undefined),
  getScalperTrades: (limit = 50, instance?: string) =>
    fetchApi<{ trades: ScalperTrade[]; count: number }>('/scalper/trades', {
      limit: String(limit),
      ...(instance ? { instance } : {}),
    }),
  getScalperStats: (days = 1, instance?: string) =>
    fetchApi<ScalperStats>('/scalper/stats', {
      days: String(days),
      ...(instance ? { instance } : {}),
    }),
  triggerScalperTick: (instance?: string) =>
    fetchApi<{ success: boolean; result: any }>('/scalper/run-now', instance ? { instance } : undefined, {
      method: 'POST',
      body: {},
    }),
  getStrategistWeekly: () => fetchApi<{ reviews: WeeklyReview[]; count: number }>('/strategist/weekly'),
  getStrategistMonthly: () => fetchApi<{ reviews: any[]; count: number }>('/strategist/monthly'),
  triggerStrategistRun: () =>
    fetchApi<{ success: boolean; result: any }>('/strategist/run-now', undefined, { method: 'POST', body: {} }),
  getStrategistStats: () => fetchApi<StrategistStats>('/strategist/stats'),
  getPortfolioHeatmap: () => fetchApi<PortfolioHeatmapData>('/strategist/portfolio-heatmap'),
  getStrategistPostmortems: (limit = 30) =>
    fetchApi<{ postmortems: PostMortemAnalysis[]; count: number }>('/strategist/postmortems', { limit: String(limit) }),
  runPortfolioBacktest: (config: { days?: number; maxPositions?: number; initialCapital?: number }) =>
    fetchApi<BacktestResult>('/backtest', undefined, {
      method: 'POST',
      body: {
        strategyType: 'portfolio',
        days: config.days ?? 90,
        config: { maxPositions: config.maxPositions ?? 10, initialCapital: config.initialCapital ?? 100000 },
        symbols: 'top30',
        initialCapital: config.initialCapital ?? 100000,
      },
    }),
  runOptimization: (config: { days?: number; initialCapital?: number }) =>
    fetchApi<OptimizationResult>('/backtest', undefined, {
      method: 'POST',
      body: { strategyType: 'optimize', days: config.days ?? 90, initialCapital: config.initialCapital ?? 100000 },
    }),

  // Position targets (TP/SL)
  getPositionTargets: async (symbols: string[]): Promise<Record<string, PositionTarget>> => {
    const resp = await fetchApi<{ targets: Record<string, PositionTarget> }>('/position-targets', {
      symbols: symbols.join(','),
    });
    return resp.targets ?? {};
  },

  // Calendar data
  getCalendarData: async (months?: number) => {
    const token = await getAuthToken();
    if (isDemoMode() || !token) {
      return getDemoCalendarData();
    }
    return fetchApi<import('../components/TradeCalendar/calendarUtils').CalendarResponse>('/strategist/calendar', {
      months: String(months ?? 0),
    });
  },

  // Live trade calendar — round-trip P&L from broker fills, FIFO-paired.
  // `from` and `to` are YYYY-MM-DD; defaults to current month server-side.
  getLiveTradeCalendar: (from?: string, to?: string, mode?: 'live' | 'paper') => {
    const params: Record<string, string> = {};
    if (from) params.from = from;
    if (to) params.to = to;
    if (mode) params.mode = mode;
    return fetchApi<import('../components/TradeCalendar/calendarUtils').CalendarResponse>(
      '/trade-calendar',
      Object.keys(params).length ? params : undefined,
    );
  },

  // AI Screener
  getSectorList: () => fetchApi<{ sectors: Record<string, string[]> }>('/ai-screener/sectors'),
  runScreener: (symbols: string[], sectorFilter?: string, minConfidence?: number, aiMode?: string) =>
    fetchApi<ScreenerResponse>('/ai-screener', undefined, {
      method: 'POST',
      body: { symbols, sectorFilter, minConfidence: minConfidence ?? 50, aiMode: aiMode ?? 'both' },
    }),
  getPositionIntel: (symbols: string[], timeframe: '1Min' | '5Min' | '15Min' | '1Hour' = '5Min') =>
    fetchApi<PositionIntelResponse>('/position-intel', undefined, {
      method: 'POST',
      body: { symbols, timeframe },
    }),

  // AI Calibration — empirical win-rate tracking for Claude's past calls
  getCalibrationSummary: (days = 30) => fetchApi<CalibrationSummary>('/calibration/summary', { days: String(days) }),
  getCalibrationCommunity: (days = 30) =>
    fetchApi<CalibrationSummary>('/calibration/community', { days: String(days) }),
  scoreCalibration: () =>
    fetchApi<{ scored: number; skipped: number; errors: number; ranAt: string }>('/calibration/score', undefined, {
      method: 'POST',
    }),

  // Per-user daily API budget — used for nav indicator + Settings page
  getUsageToday: () => fetchApi<UsageToday>('/usage/today'),

  // Daily Edge — automated pre-market + power-hour scans
  getDailyEdge: (date?: string) => {
    const params: Record<string, string> = {};
    if (date) params.date = date;
    return fetchApi<DailyEdgeResponse>('/daily-edge', Object.keys(params).length ? params : undefined);
  },
  runDailyEdgeScan: (type: 'premarket' | 'power_hour' = 'premarket') =>
    fetchApi<{ scanType: string; symbolsScanned: number; picks: number; scannedAt: string }>(
      `/daily-edge/scan?type=${type}`,
      undefined,
      { method: 'POST' },
    ),

  // Saved Screener scans
  getSavedScans: () => fetchApi<{ scans: SavedScan[] }>('/saved-scans'),
  saveScan: (payload: SavedScanInput) =>
    fetchApi<{ success: boolean; scan: SavedScan }>('/saved-scans', undefined, {
      method: 'POST',
      body: payload,
    }),
  deleteSavedScan: (scanId: string) =>
    fetchApi<{ success: boolean }>(`/saved-scans/${encodeURIComponent(scanId)}`, undefined, {
      method: 'DELETE',
    }),

  // Device tokens (push notifications — mobile wrapper)
  registerDevice: (payload: { deviceId: string; token: string; platform: 'ios' | 'android'; deviceModel?: string }) =>
    fetchApi<{ success: boolean; deviceId: string }>('/devices/register', undefined, {
      method: 'POST',
      body: payload,
    }),
  listDevices: () => fetchApi<{ devices: DeviceToken[] }>('/devices'),
  revokeDevice: (deviceId: string) =>
    fetchApi<{ success: boolean }>(`/devices/${encodeURIComponent(deviceId)}`, undefined, {
      method: 'DELETE',
    }),
};

export interface DeviceToken {
  deviceId: string;
  platform: 'ios' | 'android' | '';
  deviceModel: string;
  createdAt: string;
  lastSeenAt: string;
}

export interface SavedScan {
  id: string;
  name: string;
  sector: string | null;
  customSymbols: string | null;
  minConfidence: number;
  aiMode: 'claude' | 'gpt' | 'both';
  notifyOnMatch: boolean;
  notifyChannels: { sms: boolean; email: boolean };
  createdAt: string;
  updatedAt: string;
  lastRunAt?: string | null;
  lastMatchCount?: number;
}

export interface SavedScanInput {
  id?: string; // omit to create, include to update
  name: string;
  sector?: string | null;
  customSymbols?: string | null;
  minConfidence: number;
  aiMode: 'claude' | 'gpt' | 'both';
  notifyOnMatch?: boolean;
  notifyChannels?: { sms: boolean; email: boolean };
}

export interface PositionIntelEntry {
  intraday: ScreenerIntradayFeatures | null;
  earnings: {
    status: 'CLEAR' | 'WARN' | 'BLOCK';
    date: string | null;
    tradingDays: number | null;
    message: string;
  };
  daily: {
    rsi2: number | null;
    rsi6: number | null;
    rsi14: number | null;
    signal: string;
    changePct: number | null;
    emaTrend: 'BULLISH' | 'BEARISH' | 'FLAT' | null;
  };
  options?: ScreenerOptionsIntel | null;
  shortInterest?: ScreenerShortInterest | null;
}

export interface PositionIntelResponse {
  intel: Record<string, PositionIntelEntry>;
  fetchedAt: string;
  timeframe?: '1Min' | '5Min' | '15Min' | '1Hour';
  regime?: MarketRegime;
}

export interface WatchlistItem {
  name: string;
  symbols: string[];
  createdAt: string;
  updatedAt: string;
}

export interface ErrorLogEntry {
  source: 'validation' | 'bot' | 'strategist';
  timestamp: string;
  symbol: string;
  action: string;
  message: string;
  severity: 'error' | 'warning';
  botId?: string;
}

// Scanner types
export interface ScanResult {
  symbol: string;
  price: number;
  prev_close: number | null;
  change_pct: number;
  rsi2: number | null;
  rsi6: number | null;
  rsi14: number | null;
  volume: number | null;
  signal: 'OVERSOLD' | 'OVERBOUGHT' | 'WEAK' | 'STRONG' | 'NEUTRAL';
}

export interface ScanResponse {
  results: ScanResult[];
  count: number;
  scanned_at: string;
  market_session: any;
}

/* ── Smart Scanner types ── */

export interface SmartScanFilter {
  rsi2_oversold_enabled?: boolean;
  rsi2_below?: number;
  rsi2_overbought_enabled?: boolean;
  rsi2_above?: number;
  rsi6_oversold_enabled?: boolean;
  rsi6_below?: number;
  rsi6_overbought_enabled?: boolean;
  rsi6_above?: number;
  rsi14_enabled?: boolean;
  rsi14_min?: number;
  rsi14_max?: number;
  rvol_enabled?: boolean;
  rvol_min?: number;
  atr_move_enabled?: boolean;
  atr_move_min?: number;
  vwap_enabled?: boolean;
  vwap_position?: 'above' | 'below';
  macd_enabled?: boolean;
  macd_signal?: 'bullish' | 'bearish';
  bb_enabled?: boolean;
  bb_below?: number;
  bb_above?: number;
}

export interface SmartScanResult {
  symbol: string;
  price: number;
  prev_close: number | null;
  change_pct: number;
  volume: number | null;
  rsi2: number | null;
  rsi6: number | null;
  rsi14: number | null;
  rel_vol: number | null;
  atr_move: number | null;
  vwap_position: 'above' | 'below' | null;
  macd_signal: 'bullish' | 'bearish' | null;
  macd_histogram: number | null;
  bb_pct_b: number | null;
  composite_score: number;
  direction: 'BULL' | 'BEAR' | 'CHOP';
  recommendation: 'LONG' | 'SHORT' | 'NEUTRAL';
  signal: 'OVERSOLD' | 'OVERBOUGHT' | 'WEAK' | 'STRONG' | 'NEUTRAL';
}

export interface SmartScanResponse {
  results: SmartScanResult[];
  count: number;
  total_scanned: number;
  filters_applied: string[];
  scanned_at: string;
  market_session: any;
}

// ── Strategist Types ──

export interface StrategistConfig {
  enabled: boolean;
  autonomyLevel: 'manual' | 'semi_auto' | 'full_auto';
  maxConcurrentPositions: number;
  maxBuyingPowerPct: number;
  mode: 'paper' | 'live';
  dailyBudgetCalls: number;
  personality: 'balanced' | 'conservative' | 'momentum' | 'contrarian';
  dailyPnlTargetPct: number;
  weeklyPnlTargetPct: number;
}

export interface StrategistRule {
  id: string;
  text: string;
  source: 'Inbox' | 'Replay' | 'Post-Mortem';
  sourceTitle: string;
  status: 'pending' | 'active' | 'retired';
  createdAt: string;
  updatedAt?: string;
}

export interface StrategistAction {
  PK: string;
  SK: string;
  action: string;
  symbol: string;
  qty: number;
  reason: string;
  urgency: 'immediate' | 'today' | 'this_week';
  confidence: number;
  stopLoss?: number;
  takeProfit?: number;
  status: 'pending' | 'awaiting_approval' | 'executed' | 'rejected' | 'expired' | 'failed';
  executionMode: 'auto' | 'in_app_approval' | 'in_app_only';
  failReason?: string;
  executedAt?: string;
  orderId?: string;
  runId?: string;
  source?: 'bot' | 'strategist';
  botId?: string;
  botName?: string;
  notional?: number;
  createdAt: string;
}

export interface StrategistDecision {
  PK: string;
  SK: string;
  action: string;
  symbol: string;
  qty: number;
  reasoning: string;
  confidence: number;
  urgency: string;
  entryPrice: number;
  filledPrice?: number;
  stopLoss?: number;
  takeProfit?: number;
  takeProfit2?: number;
  takeProfit3?: number;
  status: 'open' | 'closed_profit' | 'closed_loss' | 'expired';
  exitPrice?: number;
  exitFilledQty?: number;
  exitOrderIds?: string[];
  outcomeSource?: 'broker_fills' | 'snapshot_fallback' | string;
  pnl?: number;
  pnlPct?: number;
  slippagePct?: number;
  closedAt?: string;
  createdAt: string;
  strategy?: string;
  regime?: string;
  regimeOutlook?: string;
  direction?: string;
  confidenceSizeMultiplier?: number;
  source?: string;
  activeStrategyRules?: StrategistRule[];
}

export interface ReplayTimelineItem {
  key: string;
  label: string;
  status: string;
  at?: string | null;
  summary: string;
  details?: string;
}

export interface StrategistReplay {
  decision: StrategistDecision;
  action?: StrategistAction | null;
  validation?: {
    SK: string;
    symbol: string;
    action: string;
    qty: number;
    currentPrice: number;
    valid: boolean;
    reason?: string;
    createdAt: string;
  } | null;
  run?: StrategistRun | null;
  gateLog?: {
    SK: string;
    runId: string;
    events?: Array<Record<string, any>>;
    rejections?: Record<string, any>;
    rejectionSummary?: string[];
    createdAt: string;
  } | null;
  gateEvents: Array<Record<string, any>>;
  postmortem?: PostMortemAnalysis | null;
  activeRules?: StrategistRule[];
  ruleScope?: 'decision_snapshot' | 'current_active' | 'unavailable';
  timeline: ReplayTimelineItem[];
}

export interface GateEvent {
  symbol: string;
  gate: string;
  result: 'PASSED' | 'REJECTED' | string;
  reason: string;
}

export interface GateCandidate {
  symbol: string;
  runId: string;
  gateLogSK: string;
  createdAt: string;
  lastGate: string;
  reason: string;
  rejectedAt?: string;
  rejectedPrice?: number;
  direction?: string;
  strategy?: string;
  followUpDueAt?: string;
  followUpStatus?: string;
  passedGates: string[];
  events: GateEvent[];
  rejections: Array<{
    gate: string;
    reason: string;
    rejectedAt?: string;
    rejectedPrice?: number;
    direction?: string;
    strategy?: string;
    followUpDueAt?: string;
    followUpStatus?: string;
  }>;
  followUp?: {
    outcome: 'later_winner' | 'later_loser' | 'later_flat' | 'pending' | 'untracked' | string;
    label: string;
    detail: string;
    decisionSK?: string;
    action?: string;
    createdAt?: string;
    closedAt?: string;
    hoursAfter?: number;
    pnlPct?: number;
    pnl?: number;
    entryPrice?: number;
    exitPrice?: number;
    followUpPrice1h?: number;
    followUpReturn1hPct?: number;
    maxFavorablePct?: number;
    maxAdversePct?: number;
    followUpPricedAt?: string;
  };
}

export interface GateRunSummary {
  SK: string;
  runId: string;
  createdAt: string;
  totalEvents: number;
  totalRejections: number;
}

export interface StrategistGateReview {
  candidates: GateCandidate[];
  runs: GateRunSummary[];
  gates: string[];
  count: number;
  diagnostics?: {
    logsScanned: number;
    eventsScanned: number;
    rejectionsScanned: number;
    latestGateLogAt: string | null;
    latestGateLogSK: string | null;
    hasGateLogs: boolean;
  };
}

export interface AgentBreakdownEntry {
  called: boolean;
  results: number;
  error: string | null;
  elapsed_s: number;
}

export type AgentBreakdown = Record<string, AgentBreakdownEntry>;

export interface StrategistRun {
  PK: string;
  SK: string;
  symbolsScanned: number;
  actionsEmitted: number;
  marketAssessment: string;
  error?: string;
  execResults?: { executed: number; queued: number; notified: number; errors: number };
  agentBreakdown?: AgentBreakdown;
  createdAt: string;
}

export type SectorClassification = 'HOT' | 'WARM' | 'NEUTRAL' | 'COOL' | 'COLD';

export interface SectorSentiment {
  sector: string;
  anchorEtf: string | null;
  change1dPct: number;
  change5dPct: number;
  breadthPct: number;
  volumeRatio: number;
  classification: SectorClassification;
  leaders: string[];
  componentCount: number;
}

export interface SectorSentimentResponse {
  sectors: SectorSentiment[];
  count: number;
  runId: string | null;
  updatedAt: string | null;
}

export interface ScalperInstance {
  key: string;
  symbol: string;
  assetClass: 'equity' | 'crypto';
  label: string;
  enabled: boolean;
}

export interface ScalperPrice {
  symbol: string;
  label: string;
  assetClass: 'equity' | 'crypto';
  price: number;
  changePct: number;
  asOf: string | null;
}

export interface ScalperPricesResponse {
  prices: Record<string, ScalperPrice>;
  updatedAt: string | null;
  credentialsConfigured?: boolean;
}

export interface ScalperConfig {
  instance: string;
  enabled: boolean;
  symbol: string;
  assetClass: 'equity' | 'crypto';
  label: string;
  updatedAt: string | null;
  credentialsConfigured: boolean;
}

export interface ScalperPosition {
  symbol: string;
  side: 'long' | 'short';
  qty: number;
  entry_price: number;
  entry_time: string;
  initial_stop: number;
  initial_target: number;
  current_stop: number;
  session_extreme: number;
  scaled_out: boolean;
  atr_at_entry: number;
}

export interface ScalperDecision {
  action: string;
  confidence: number;
  reasoning: string;
}

export interface ScalperTick {
  SK: string;
  createdAt: string;
  symbol: string;
  price: number;
  action: string;
  confidence: number;
  reasoning: string;
  indicators: Record<string, number | string>;
}

export interface ScalperCircuitBreaker {
  armed: boolean;
  loss_count: number;
  expires_at: string | null;
}

export interface ScalperActiveConfig {
  initialStopAtr: number;
  scaleOutTpAtr: number;
  scaleOutFraction: number;
  trailTiers: { profitAtr: number; trailAtr: number }[];
  entryConfidenceMin: number;
  phaseFloors: Record<string, number>;
  circuitBreaker: {
    lossThreshold: number;
    floorBump: number;
    durationMin: number;
  };
}

export interface ScalperEdgeMapBucket {
  count: number;
  wins: number;
  win_rate: number;
  avg_pnl: number;
  total_pnl: number;
}

export interface ScalperEdgeMap {
  total: number;
  lookback_days: number;
  by_side_phase: Record<string, ScalperEdgeMapBucket>;
  by_side_trend: Record<string, ScalperEdgeMapBucket>;
  by_side: Record<string, ScalperEdgeMapBucket>;
  suppressed: boolean;
}

export interface ScalperStatus {
  instance: string;
  enabled: boolean;
  symbol: string;
  assetClass: 'equity' | 'crypto';
  label: string;
  credentialsConfigured: boolean;
  updatedAt: string | null;
  position: ScalperPosition | null;
  lastDecision: ScalperDecision | null;
  recentTicks: ScalperTick[];
  decisionEngine?: 'sonnet' | 'rules';
  circuitBreaker?: ScalperCircuitBreaker;
  activeConfig?: ScalperActiveConfig;
  edgeMap?: ScalperEdgeMap;
}

export interface ScalperTrade {
  SK: string;
  symbol: string;
  side: 'long' | 'short';
  qty: number;
  entryPrice: number;
  exitPrice: number;
  entryTime: string;
  exitTime: string;
  pnlDollars: number;
  pnlPct: number;
  holdMinutes: number;
  exitReason: string;
  scaledOut: boolean;
  partial?: boolean;
  reasoning: string;
}

export interface ScalperStats {
  days: number;
  trades: number;
  wins: number;
  losses: number;
  totalPnl: number;
  lockedPnl?: number;
  winRate: number;
  avgHoldMinutes: number;
  bestPnl: number;
  worstPnl: number;
}

export interface PortfolioHeatmapData {
  symbols: string[];
  sectors: Record<string, string>;
  matrix: number[][];
  positions: {
    symbol: string;
    sector: string;
    side: string;
    market_value: number;
    unrealized_plpc: number;
  }[];
  clusteredGroups: string[][];
  riskWarnings: string[];
  sectorExposure: Record<string, { value: number; pct: number }>;
  error?: string;
}

export interface StrategistStats {
  totalTrades: number;
  winRate: number;
  avgPnlPct: number;
  bestPnlPct: number;
  worstPnlPct: number;
  longWinRate: number;
  shortWinRate: number;
  longCount: number;
  shortCount: number;
  todayCalls: number;
  todayInputTokens?: number;
  todayOutputTokens?: number;
  lastRunAt?: string;
  lastMarketAssessment?: string;
  lastAgentBreakdown?: AgentBreakdown;
  readinessScore?: number;
  readinessLevel?: 'not_ready' | 'developing' | 'almost_ready' | 'ready';
  readinessBreakdown?: { sampleSize: number; winRate: number; profitability: number; consistency: number };
  readinessAdvice?: string[];
  sharpe?: number;
  maxDrawdown?: number;
  avgHoldDays?: number;
  perStrategyBreakdown?: Record<string, { trades: number; wins: number; winRate: number }>;
}

export interface WeeklyReview {
  PK: string;
  SK: string;
  summary: string;
  longBiasAdj: string;
  shortBiasAdj: string;
  sectorNotes: string;
  riskAdj: string;
  keyLesson: string;
  trackRecord: { totalTrades: number; winRate: number; avgPnlPct: number };
  createdAt: string;
}

export interface PostMortemAnalysis {
  PK: string;
  SK: string;
  decisionSK: string;
  symbol: string;
  strategy: string;
  action: string;
  pnlPct: number;
  regime: string;
  outcomeCategory: string; // thesis_correct|thesis_wrong|bad_timing|bad_sizing|external_shock|early_exit|late_exit
  whatHappened: string;
  signalMissed: string;
  lesson: string;
  confidenceCalibrated: boolean;
  suggestedAdjustment: string;
  createdAt: string;
}

export interface OptimizationResult {
  strategy: string;
  symbolCount: number;
  days: number;
  bestConfig: Record<string, number>;
  defaultMetrics: BacktestMetricsResult;
  optimizedMetrics: BacktestMetricsResult;
  paramSensitivity: Array<{
    param: string;
    value: number;
    sharpe: number;
    winRate: number;
    totalReturn: number;
    trades: number;
  }>;
  inSampleRange: { start: string; end: string };
  outOfSampleRange: { start: string; end: string };
  optimizedEquityCurve: Array<{ date: string; equity: number }>;
  optimizedTrades: BacktestTrade[];
  strategyBreakdown: Record<
    string,
    { trades: number; wins: number; winRate: number; pnl: number; longs: number; shorts: number }
  >;
  elapsed: number;
  runsCompleted: number;
}

export interface PositionTarget {
  stopLoss: number | null;
  takeProfit: number | null; // TP1
  takeProfit2: number | null; // TP2
  takeProfit3: number | null; // TP3
  source: 'strategist' | 'quick_trade';
  updatedAt: string;
}

export type OrderType = 'market' | 'limit' | 'stop' | 'stop_limit' | 'trailing_stop';

export interface PlaceOrderRequest {
  symbol: string;
  side: 'buy' | 'sell';
  notional?: number;
  qty?: number;
  time_in_force?: string;
  mode?: string;
  order_type?: OrderType;
  // Required by order_type:
  //   limit, stop_limit  → limit_price
  //   stop, stop_limit   → stop_price
  //   trailing_stop      → trail_percent OR trail_price
  limit_price?: number;
  stop_price?: number;
  trail_percent?: number;
  trail_price?: number;
  // Bracket / OTO — when supplied, the order attaches a broker-enforced
  // stop-loss and/or take-profit child order. Requires qty (not notional)
  // and forces time_in_force to 'gtc'.
  stop_loss?: number;
  take_profit?: number;
}

export interface PlaceOrderResponse {
  success?: boolean;
  order?: {
    id: string;
    symbol: string;
    side: string;
    qty: string;
    notional: string;
    type: string;
    status: string;
    filled_avg_price: string | null;
    order_class?: string;
    legs?: Array<{
      id: string;
      type: string;
      stop_price: string | null;
      limit_price: string | null;
      status: string;
    }>;
  };
  error?: string;
  detail?: string;
}
